import React, {useState, useEffect, useMemo} from 'react';
import { Helmet } from 'react-helmet-async';
import { auth } from '../firebase';
import LoginModal from './LoginModal';
import heroImage from '../assets/hero-image.png';
import appIcon from '../assets/icon.png';
import translations, { detectLang } from '../locales/translations';

const faqData = [
  {
    question: "What is Forecast?",
    answer: "Forecast is a personal finance app built around a calendar. It shows your projected bank balance for every day ahead, but it also tracks your budgets, savings goals, debt, net worth, and spending patterns — all in one place. Think of it as the financial command center you've always wanted."
  },
  {
    question: "How does balance forecasting work?",
    answer: "Enter your current balance, then add your recurring bills, paychecks, and expected expenses. Forecast calculates your projected balance for each day so you can see weeks or months ahead. Connect your bank and it stays updated automatically."
  },
  {
    question: "What can I track besides bills and income?",
    answer: "Budgets by category, savings goals with progress tracking, debt and liabilities with interest rates, assets, net worth over time, and detailed spending breakdowns. Forecast gives you the full picture, not just a calendar."
  },
  {
    question: "Is Forecast free to use?",
    answer: "Forecast offers a free tier with core budgeting and calendar features. Premium features like bank sync, advanced insights, and unlimited accounts are available with a subscription."
  },
  {
    question: "Can I connect my bank account?",
    answer: "Yes! Forecast supports secure bank connections through Plaid, so your transactions sync automatically. You can also enter transactions manually if you prefer."
  },
  {
    question: "Is my financial data secure?",
    answer: "Absolutely. Forecast uses bank-level encryption and never stores your bank login credentials. Bank connections are handled through Plaid, a trusted financial data platform used by major fintech companies."
  }
];

const jsonLdSoftwareApp = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Forecast",
  "applicationCategory": "FinanceApplication",
  "operatingSystem": "iOS, Android, Web",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "description": "Personal finance app that forecasts your balance, tracks budgets, goals, debt, and net worth — all built around a calendar.",
  "url": "https://tryforecast.app"
};

const jsonLdOrganization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Forecast",
  "url": "https://tryforecast.app",
  "logo": "https://tryforecast.app/icon.png"
};

const jsonLdFaq = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

const LandingPage = () => {
  const tr = useMemo(() => translations[detectLang()], []);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [loginReason, setLoginReason] = useState('');
  const [selectedTier, setSelectedTier] = useState('monthly');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      if (user) {
        // If user is logged in, direct them to dashboard regardless of reason
        if (loginReason) {
          window.location.href = '/dashboard';
          setLoginReason('');
        }
      }
      console.log('Auth state changed:', user ? 'Logged in as ' + user.email : 'Logged out');
    });
  
    return () => unsubscribe();
  }, [loginReason]);

  const handleDownload = () => {
    const user = auth.currentUser;
  
    if (!user) {
      // Open login modal and set reason to 'download'
      setLoginReason('download');
      setIsLoginModalOpen(true);
      return;
    }
    
    // If user is already logged in, redirect to dashboard
    window.location.href = '/dashboard';
  };

  const handleSubscribe = async () => {
    const user = auth.currentUser;
  
    if (!user) {
      // Open login modal and set reason to 'subscribe'
      setLoginReason('subscribe');
      setIsLoginModalOpen(true);
      return;
    }

    handleSubscribeAfterLogin();
  };

  const handleSubscribeAfterLogin = async () => {
    try {
      console.log('Starting checkout process...');
      const response = await fetch('/.netlify/functions/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: auth.currentUser.uid,
          pricingTier: selectedTier
        })
      });
      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No URL in response:', data);
      }
    } catch (error) {
      console.error('Error during checkout:', error);
    }
  };

  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
    setLoginReason('');
  };

  return (
    <div style={{minHeight: '100vh', backgroundColor: '#f5f5f5'}}>
      <Helmet>
        <title>Forecast — Your Money, Mapped Out | Budgets, Goals, Bank Sync & More</title>
        <meta name="description" content="Forecast is a personal finance app that shows your projected balance, tracks budgets and spending, sets savings goals, manages debt, and syncs with your bank — all built around a calendar you'll actually use." />
        <link rel="canonical" href="https://tryforecast.app/" />
        <meta property="og:title" content="Forecast — Your Money, Mapped Out" />
        <meta property="og:description" content="See your projected balance, track budgets, set savings goals, manage debt, and sync your bank — all in one app built around a calendar." />
        <meta property="og:url" content="https://tryforecast.app/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Forecast — Your Money, Mapped Out" />
        <meta name="twitter:description" content="See your projected balance, track budgets, set savings goals, manage debt, and sync your bank — all in one app built around a calendar." />
        <script type="application/ld+json">{JSON.stringify(jsonLdSoftwareApp)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdOrganization)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>
      </Helmet>
      <nav style={{
        backgroundColor: 'white', 
        padding: '0.75rem 1rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {/* Navigation content */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
             <img 
                src={appIcon} 
                alt="Forecast App Icon" 
                style={{
                  width: '32px',
                  height: '32px',
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                }}
              />
              <span style={{ 
                fontSize: '1.5rem', 
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}>
                Forecast
              </span>
            </div>
          </div>

          <div style={{
            marginLeft: '1rem',
            flexShrink: 0
          }}>
            {user ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{
                  color: '#666',
                  fontSize: '0.875rem',
                  maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>{user.email}</span>
                <button 
                  onClick={() => auth.signOut()} 
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem'
                  }}
                >
                  {tr.nav.signOut}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsLoginModalOpen(true)}
                style={{
                  padding: '0.5rem 0.75rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontSize: '0.875rem'
                }}
              >
                {tr.nav.signIn}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Subscription Management Banner */}
      {window.location.search.includes('manage_subscription') && (
        <div style={{
          backgroundColor: '#dbeafe',
          padding: '1rem',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#1e40af',
            marginBottom: '0.5rem'
          }}>
            {tr.banner.message}
          </p>
          <button
            onClick={() => setIsLoginModalOpen(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            {tr.banner.button}
          </button>
        </div>
      )}

      {/* Hero Section with Added Image */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '1rem 1rem' : '4rem 1rem',
        textAlign: 'center'
      }}>
        
        <h1 style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          marginBottom: '1.5rem',
          lineHeight: 1.1
        }}>
          {tr.hero.headline}
        </h1>

        <p style={{
          fontSize: '1.25rem',
          color: '#666',
          maxWidth: '800px',
          margin: '0 auto 2rem'
        }}>
          {tr.hero.sub}
        </p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          marginBottom: '3rem'
        }}>
          <button
            onClick={handleDownload}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            {tr.hero.download}
          </button>
          <button
            onClick={() => setIsLoginModalOpen(true)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#dbeafe',
              color: '#2563eb',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            {tr.hero.login}
          </button>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <img 
            src={heroImage} 
            alt="Forecast App Interface"
            style={{
              width: '100%',
              maxWidth: '1000px',
              height: 'auto',
              padding: '0 16px'
            }}
          />
        </div>
       
      </div>

      {/* Features Section */}
      <div style={{
        backgroundColor: 'white',
        padding: '4rem 1rem'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {tr.features.map((feature, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>{feature.title}</h3>
                <p style={{ color: '#666' }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '4rem 1rem'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            {tr.faq.title}
          </h2>
          {tr.faq.items.map((faq, index) => (
            <div key={index} style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              marginBottom: '1rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                marginBottom: '0.5rem'
              }}>
                {faq.q}
              </h3>
              <p style={{
                color: '#666',
                lineHeight: 1.6,
                margin: 0
              }}>
                {faq.a}
              </p>
            </div>
          ))}
        </div>
      </div>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
      />
    </div>
  );
};

export default LandingPage;